import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { Badge } from "@lawline/ui/components/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@lawline/ui/components/table";
import { cn } from "@lawline/ui/core/cn";

import { formatDate } from "@platform/fmt";

import type { AdminUser, UserRole } from "../users.types";
import { UserRoleActions } from "./user-role-actions";

const ROLE_CLASS: Record<UserRole, string> = {
	admin: "bg-primary/15 text-primary",
	user: "bg-muted text-muted-foreground",
};

export function UsersTable({ users }: { users: AdminUser[] }) {
	const t = useTranslations("admin.users.table");
	const tRoot = useTranslations("admin.users");

	const columns = useMemo<ColumnDef<AdminUser>[]>(
		() => [
			{
				accessorKey: "name",
				header: t("columns.name"),
				cell: ({ row }) => <span className="font-medium">{row.original.name || "-"}</span>,
			},
			{
				accessorKey: "email",
				header: t("columns.email"),
			},
			{
				id: "role",
				header: t("columns.role"),
				cell: ({ row }) => {
					const role = row.original.role ?? "user";
					return (
						<Badge className={cn("border-transparent", ROLE_CLASS[role])}>
							{t(`roles.${role}`)}
						</Badge>
					);
				},
			},
			{
				accessorKey: "createdAt",
				header: t("columns.created"),
				cell: ({ row }) => formatDate(row.original.createdAt),
			},
			{
				id: "actions",
				header: "",
				cell: ({ row }) => (
					<div className="text-right">
						<UserRoleActions user={row.original} />
					</div>
				),
			},
		],
		[t],
	);

	const table = useReactTable({
		data: users,
		columns,
		getCoreRowModel: getCoreRowModel(),
	});

	return (
		<div className="overflow-hidden rounded-md border">
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((group) => (
						<TableRow key={group.id}>
							{group.headers.map((header) => (
								<TableHead key={header.id}>
									{flexRender(header.column.columnDef.header, header.getContext())}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.length === 0 ? (
						<TableRow>
							<TableCell
								colSpan={columns.length}
								className="text-muted-foreground h-20 text-center text-sm"
							>
								{tRoot("emptyState")}
							</TableCell>
						</TableRow>
					) : (
						table.getRowModel().rows.map((row) => (
							<TableRow key={row.id}>
								{row.getVisibleCells().map((cell) => (
									<TableCell key={cell.id}>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</TableCell>
								))}
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
	);
}
